import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  Alert,
  Box,
  Chip,
  CircularProgress,
  Divider,
  FormControl,
  Grid,
  MenuItem,
  Select,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import { LineChart } from "@mui/x-charts/LineChart";
import { useTheme } from "@mui/material/styles";
import PaidOutlinedIcon from "@mui/icons-material/PaidOutlined";
import ReceiptLongOutlinedIcon from "@mui/icons-material/ReceiptLongOutlined";
import PhoneIphoneRoundedIcon from "@mui/icons-material/PhoneIphoneRounded";
import TrendingUpRoundedIcon from "@mui/icons-material/TrendingUpRounded";
import StarRoundedIcon from "@mui/icons-material/StarRounded";
import DevicesOutlinedIcon from "@mui/icons-material/DevicesOutlined";
import LanguageOutlinedIcon from "@mui/icons-material/LanguageOutlined";
import PhoneIphoneOutlinedIcon from "@mui/icons-material/PhoneIphoneOutlined";
import { useTranslation } from "react-i18next";
import { getStoreAnalytics } from "../../api/analyticsService";
import { getBusinessOrders } from "../../api/orderService";
import { getMyStores } from "../../api/storeService";
import { formatCurrency, formatDate, OrderStatusByName } from "../Admin/constants";
import {
  PeriodSelector,
  PERIOD_OPTIONS,
  SectionCard,
  EmptyState,
  TrafficSourceBars,
  ProductRow,
  TopProductsCard,
  formatCount,
  formatDayLabel,
} from "../Admin/analyticsShared";
import StatCard from "./StatCard";

const EMPTY_VALUES = new Set(["0", "0%", "-", "0m"]);

function MiniStat({ label, value, tone = "default" }) {
  const isEmpty = EMPTY_VALUES.has(String(value));
  const color = isEmpty
    ? "text.disabled"
    : tone === "success"
    ? "success.main"
    : tone === "error"
    ? "error.main"
    : "text.primary";

  return (
    <Box sx={{ py: 0.5 }}>
      <Typography
        sx={{
          fontSize: { xs: 22, sm: 26 },
          fontWeight: 800,
          letterSpacing: "-0.01em",
          lineHeight: 1.2,
          color,
        }}
        noWrap
      >
        {value}
      </Typography>
      <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.25 }} noWrap>
        {label}
      </Typography>
    </Box>
  );
}

export default function Dashboard() {
  const { t } = useTranslation(["boDashboard", "boSidebar", "boShared", "common"]);
  const theme = useTheme();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const storeId = searchParams.get("storeId") || "all";

  const PLATFORM_FILTERS = [
    { value: "all", label: t("boDashboard:platformBoth"), icon: DevicesOutlinedIcon },
    { value: "Web", label: t("boDashboard:platformWebsite"), icon: LanguageOutlinedIcon },
    { value: "Mobile", label: t("boDashboard:platformMobileApp"), icon: PhoneIphoneOutlinedIcon },
  ];

  const [stores, setStores] = useState([]);
  const [period, setPeriod] = useState("30d");
  const [customStart, setCustomStart] = useState("");
  const [customEnd, setCustomEnd] = useState("");
  const [chartMetric, setChartMetric] = useState("revenue");
  const [platformFilter, setPlatformFilter] = useState("all");
  const [data, setData] = useState(null);
  const [recentOrders, setRecentOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    getMyStores()
      .then(setStores)
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (period === "custom" && (!customStart || !customEnd)) return;

    let active = true;
    setLoading(true);
    getStoreAnalytics({
      storeId: storeId === "all" ? undefined : storeId,
      period,
      startDate: period === "custom" ? customStart : undefined,
      endDate: period === "custom" ? customEnd : undefined,
      platform: platformFilter === "all" ? undefined : platformFilter,
    })
      .then((result) => {
        if (!active) return;
        setData(result);
        setError("");
        return getBusinessOrders({ storeId: storeId === "all" ? undefined : storeId, pageSize: 8 })
          .then((orders) => {
            if (active) setRecentOrders(orders.items ?? []);
          })
          .catch(() => {});
      })
      .catch(() => {
        if (active) setError(t("boDashboard:loadError"));
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [storeId, period, customStart, customEnd, platformFilter]);

  const periodLabel = useMemo(() => {
    if (period === "custom") {
      return customStart && customEnd ? `${customStart} - ${customEnd}` : t("boDashboard:customRange");
    }
    return PERIOD_OPTIONS.find((p) => p.value === period)?.label ?? "";
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [period, customStart, customEnd]);

  const chartSeries = useMemo(() => {
    if (!data?.overview) return { labels: [], values: [] };
    const points = chartMetric === "revenue" ? data.overview.revenueSeries : data.overview.visitsSeries;
    if (!points) return { labels: [], values: [] };
    return {
      labels: points.map((p) => formatDayLabel(p.date)),
      values: points.map((p) => (chartMetric === "revenue" ? p.amount : p.count)),
    };
  }, [data, chartMetric]);

  const hasProductHealthIssues =
    data?.productsHealth &&
    (data.productsHealth.lowStock.length > 0 ||
      data.productsHealth.highViewsZeroSales.length > 0 ||
      data.productsHealth.zeroViews.length > 0);

  return (
    <Box>
      <Stack
        direction={{ xs: "column", md: "row" }}
        spacing={2}
        justifyContent="space-between"
        alignItems={{ xs: "flex-start", md: "center" }}
        sx={{ mb: 3 }}
      >
        <Box>
          <Typography variant="h4">{t("boSidebar:dashboard")}</Typography>
          <Typography variant="body2" color="text.secondary">
            {stores.length > 1 ? t("boDashboard:subtitleMulti") : t("boDashboard:subtitleSingle")}
          </Typography>
        </Box>
        <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} alignItems={{ xs: "stretch", sm: "center" }}>
          {stores.length > 1 && (
            <FormControl size="small" sx={{ minWidth: 200 }}>
              <Select
                value={storeId}
                onChange={(e) =>
                  setSearchParams(e.target.value === "all" ? {} : { storeId: e.target.value })
                }
              >
                <MenuItem value="all">{t("boShared:allStores")}</MenuItem>
                {stores.map((s) => (
                  <MenuItem key={s.storeId} value={s.storeId}>
                    {s.storeName}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
          <ToggleButtonGroup
            value={platformFilter}
            exclusive
            size="small"
            onChange={(e, value) => {
              if (value) setPlatformFilter(value);
            }}
          >
            {PLATFORM_FILTERS.map(({ value, label, icon: Icon }) => (
              <ToggleButton key={value} value={value} sx={{ gap: 0.75 }}>
                <Icon fontSize="small" />
                {label}
              </ToggleButton>
            ))}
          </ToggleButtonGroup>
          <PeriodSelector
            period={period}
            onPeriodChange={setPeriod}
            customStart={customStart}
            customEnd={customEnd}
            onCustomChange={(start, end) => {
              setCustomStart(start);
              setCustomEnd(end);
            }}
          />
        </Stack>
      </Stack>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {loading && !data ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
          <CircularProgress />
        </Box>
      ) : data ? (
        <>
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid size={{ xs: 12, sm: 6, lg: 3 }}>
              <StatCard
                icon={PaidOutlinedIcon}
                label={t("boDashboard:revenue")}
                subtitle={t("boDashboard:revenueSubtitle")}
                value={formatCurrency(data.overview.revenueCurrent)}
                changePercent={data.overview.revenueChangePercent}
                color="primary"
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6, lg: 3 }}>
              <StatCard
                icon={ReceiptLongOutlinedIcon}
                label={t("boDashboard:orders")}
                subtitle={t("boDashboard:ordersSubtitle")}
                value={formatCount(data.overview.ordersCurrent)}
                changePercent={data.overview.ordersChangePercent}
                color="secondary"
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6, lg: 3 }}>
              <StatCard
                icon={PhoneIphoneRoundedIcon}
                label={t("boDashboard:visitors")}
                subtitle={t("boDashboard:visitorsSubtitle")}
                value={formatCount(data.overview.visitorsCurrent)}
                changePercent={data.overview.visitorsChangePercent}
                color="info"
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6, lg: 3 }}>
              <StatCard
                icon={TrendingUpRoundedIcon}
                label={t("boDashboard:conversionRate")}
                subtitle={t("boDashboard:conversionRateSubtitle")}
                value={`${data.overview.conversionRateCurrent}%`}
                changePercent={data.overview.conversionRateChangePercent}
                color="success"
              />
            </Grid>
          </Grid>

          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid size={{ xs: 12, lg: 8 }}>
              <SectionCard
                title={chartMetric === "revenue" ? t("boDashboard:revenueOverTime") : t("boDashboard:storeVisitsOverTime")}
                action={
                  <ToggleButtonGroup
                    size="small"
                    exclusive
                    value={chartMetric}
                    onChange={(e, v) => v && setChartMetric(v)}
                  >
                    <ToggleButton value="revenue">
                      {t("boDashboard:revenue")}
                    </ToggleButton>
                    <ToggleButton value="visits">
                      {t("boDashboard:visits")}
                    </ToggleButton>
                  </ToggleButtonGroup>
                }
              >
                {chartSeries.values.length > 0 ? (
                  <LineChart
                    height={260}
                    series={[
                      {
                        data: chartSeries.values,
                        color: theme.palette.primary.dark,
                        area: true,
                        showMark: true,
                        valueFormatter: (v) => (chartMetric === "revenue" ? formatCurrency(v) : v),
                      },
                    ]}
                    xAxis={[{ scaleType: "point", data: chartSeries.labels }]}
                    yAxis={[{ width: 50 }]}
                    grid={{ horizontal: true }}
                    margin={{ left: 0, right: 10, top: 20, bottom: 20 }}
                    sx={{
                      "& .MuiLineElement-root": { strokeWidth: 2.5 },
                      "& .MuiAreaElement-root": { fill: "url(#dashboardAreaGradient)" },
                      "& .MuiMarkElement-root": { stroke: theme.palette.primary.dark, fill: "#fff", strokeWidth: 2 },
                      "& .MuiChartsAxis-line, & .MuiChartsAxis-tick": { stroke: theme.palette.divider },
                    }}
                  >
                    <defs>
                      <linearGradient id="dashboardAreaGradient" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor={theme.palette.primary.main} stopOpacity={0.32} />
                        <stop offset="100%" stopColor={theme.palette.primary.main} stopOpacity={0.02} />
                      </linearGradient>
                    </defs>
                  </LineChart>
                ) : (
                  <EmptyState text={t("boDashboard:noData")} />
                )}
              </SectionCard>
            </Grid>
            <Grid size={{ xs: 12, lg: 4 }}>
              <SectionCard title={t("boDashboard:trafficSources")}>
                <TrafficSourceBars sources={data.traffic.sources} />
              </SectionCard>
            </Grid>
          </Grid>

          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid size={{ xs: 12, md: 6 }}>
              <TopProductsCard data={data.topProducts} subtitle={t("boDashboard:topProductsSubtitle")} />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <SectionCard title={t("boDashboard:recentOrders")}>
                {recentOrders.length > 0 ? (
                  <Stack divider={<Divider />}>
                    {recentOrders.map((order) => {
                      const statusInfo = OrderStatusByName[order.status] || { label: order.status, color: "default" };
                      const itemCount = order.items?.length || 0;
                      const hasCoupon = order.discountAmount > 0;
                      const hasWholesaleItem = order.items?.some((i) => i.isWholesale);
                      return (
                        <Stack
                          key={order.orderId}
                          direction="row"
                          alignItems="center"
                          spacing={1.5}
                          sx={{ py: 1, cursor: "pointer" }}
                          onClick={() => navigate(`/business-owner/orders?orderId=${order.orderId}`)}
                        >
                          <Box sx={{ minWidth: 0, flexGrow: 1 }}>
                            <Stack direction="row" alignItems="center" spacing={1} sx={{ flexWrap: "wrap", rowGap: 0.5 }}>
                              <Typography variant="body2" fontWeight={700} noWrap>
                                {order.orderNumber}
                              </Typography>
                              <Chip label={statusInfo.label} size="small" color={statusInfo.color} sx={{ height: 20, fontSize: 11 }} />
                              {hasWholesaleItem && (
                                <Chip label={t("boShared:wholesale")} size="small" variant="outlined" color="secondary" sx={{ height: 20, fontSize: 11 }} />
                              )}
                              {hasCoupon && (
                                <Chip
                                  label={order.couponCode || t("boShared:coupon")}
                                  size="small"
                                  variant="outlined"
                                  color="success"
                                  sx={{ height: 20, fontSize: 11 }}
                                />
                              )}
                            </Stack>
                            <Typography variant="caption" color="text.secondary" noWrap sx={{ display: "block" }}>
                              {order.storeName} &middot; {t("boShared:itemCount", { count: itemCount })} &middot; {formatDate(order.createdAt)}
                            </Typography>
                          </Box>
                          <Typography variant="body2" fontWeight={700} sx={{ flexShrink: 0 }}>
                            {formatCurrency(order.totalAmount)}
                          </Typography>
                        </Stack>
                      );
                    })}
                  </Stack>
                ) : (
                  <EmptyState text={t("boDashboard:noOrdersYet")} />
                )}
              </SectionCard>
            </Grid>
          </Grid>

          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid size={{ xs: 12, md: 4 }}>
              <SectionCard
                title={t("boDashboard:conversionTitle")}
                action={
                  <Typography
                    variant="caption"
                    noWrap
                    sx={{ fontWeight: 700, letterSpacing: "0.06em", textTransform: "uppercase", color: "text.disabled" }}
                  >
                    {periodLabel}
                  </Typography>
                }
              >
                <Grid container columnSpacing={2}>
                  <Grid size={6}>
                    <MiniStat label={t("boDashboard:storeVisits")} value={formatCount(data.traffic.storeVisits)} />
                  </Grid>
                  <Grid size={6}>
                    <MiniStat label={t("boDashboard:productViews")} value={formatCount(data.traffic.productViewsTotal)} />
                  </Grid>
                </Grid>
                <Box sx={{ height: "1px", bgcolor: "divider", my: 1.75 }} />
                <Grid container columnSpacing={2}>
                  <Grid size={6}>
                    <MiniStat label={t("boDashboard:addToCartRate")} value={`${data.conversion.addToCartRate}%`} tone="success" />
                  </Grid>
                  <Grid size={6}>
                    <MiniStat label={t("boDashboard:cartAbandonment")} value={`${data.conversion.cartAbandonmentRate}%`} tone="error" />
                  </Grid>
                </Grid>
              </SectionCard>
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <SectionCard title={t("boDashboard:customersTitle")}>
                <Grid container columnSpacing={2}>
                  <Grid size={4}>
                    <MiniStat label={t("boDashboard:newBuyers")} value={formatCount(data.customers.newBuyers)} />
                  </Grid>
                  <Grid size={4}>
                    <MiniStat label={t("boDashboard:returning")} value={formatCount(data.customers.returningBuyers)} />
                  </Grid>
                  <Grid size={4}>
                    <MiniStat label={t("boDashboard:followersLabel")} value={formatCount(data.customers.followerCount)} />
                  </Grid>
                </Grid>
                {data.customers.byCity.length > 0 && (
                  <Stack
                    spacing={0.75}
                    divider={<Box sx={{ borderBottom: "1px solid", borderColor: "divider" }} />}
                    sx={{ mt: 2 }}
                  >
                    {data.customers.byCity.slice(0, 5).map((c) => (
                      <Stack key={c.city} direction="row" justifyContent="space-between" alignItems="center" sx={{ width: "100%", py: 0.25 }}>
                        <Typography variant="body2" color="text.secondary" dir="auto" noWrap sx={{ minWidth: 0 }}>
                          {c.city}
                        </Typography>
                        <Typography variant="body2" fontWeight={700} sx={{ flexShrink: 0, pl: 1 }}>
                          {c.orderCount}
                        </Typography>
                      </Stack>
                    ))}
                  </Stack>
                )}
              </SectionCard>
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <SectionCard title={t("boDashboard:ratingsEngagement")}>
                <Grid container columnSpacing={2}>
                  <Grid size={4}>
                    <MiniStat label={t("boDashboard:avgRating")} value={data.engagement.averageRating > 0 ? data.engagement.averageRating.toFixed(1) : "-"} />
                  </Grid>
                  <Grid size={4}>
                    <MiniStat label={t("common:messages")} value={formatCount(data.engagement.messageCount)} />
                  </Grid>
                  <Grid size={4}>
                    <MiniStat
                      label={t("boDashboard:avgResponse")}
                      value={data.engagement.averageResponseTimeMinutes != null ? `${Math.round(data.engagement.averageResponseTimeMinutes)}m` : "-"}
                    />
                  </Grid>
                </Grid>
                {data.engagement.recentReviews.length > 0 ? (
                  <Stack spacing={1} divider={<Divider />} sx={{ mt: 2 }}>
                    {data.engagement.recentReviews.slice(0, 3).map((r) => (
                      <Box key={r.storeReviewId}>
                        <Stack direction="row" spacing={0.5} alignItems="center">
                          <Typography variant="body2" fontWeight={600}>
                            {r.customerName}
                          </Typography>
                          <StarRoundedIcon sx={{ fontSize: 14, color: "warning.main" }} />
                          <Typography variant="caption">{r.rating}</Typography>
                        </Stack>
                        {r.comment && (
                          <Typography variant="caption" color="text.secondary" sx={{ display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden" }}>
                            {r.comment}
                          </Typography>
                        )}
                      </Box>
                    ))}
                  </Stack>
                ) : (
                  <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 2 }}>
                    {t("boDashboard:noRatingsYet")}
                  </Typography>
                )}
              </SectionCard>
            </Grid>
          </Grid>

          {hasProductHealthIssues && (
            <SectionCard title={t("boDashboard:productHealthTitle")} subtitle={t("boDashboard:productHealthSubtitle")}>
              <Grid container spacing={2}>
                {data.productsHealth.lowStock.length > 0 && (
                  <Grid size={{ xs: 12, md: 4 }}>
                    <Typography variant="body2" fontWeight={700} sx={{ mb: 1 }}>
                      {t("boDashboard:lowStock")}
                    </Typography>
                    <Stack divider={<Divider />}>
                      {data.productsHealth.lowStock.slice(0, 5).map((p) => (
                        <ProductRow
                          key={p.productId}
                          product={p}
                          metricLabel={t("boDashboard:lowStock")}
                          metricValue={t("boDashboard:stockLeft", { count: p.stockQuantity })}
                        />
                      ))}
                    </Stack>
                  </Grid>
                )}
                {data.productsHealth.highViewsZeroSales.length > 0 && (
                  <Grid size={{ xs: 12, md: 4 }}>
                    <Typography variant="body2" fontWeight={700} sx={{ mb: 1 }}>
                      {t("boDashboard:highViewsNoSales")}
                    </Typography>
                    <Stack divider={<Divider />}>
                      {data.productsHealth.highViewsZeroSales.slice(0, 5).map((p) => (
                        <ProductRow
                          key={p.productId}
                          product={p}
                          metricLabel={t("boDashboard:viewsCount", { count: p.viewCount })}
                          metricValue={t("boDashboard:zeroSold")}
                        />
                      ))}
                    </Stack>
                  </Grid>
                )}
                {data.productsHealth.zeroViews.length > 0 && (
                  <Grid size={{ xs: 12, md: 4 }}>
                    <Typography variant="body2" fontWeight={700} sx={{ mb: 1 }}>
                      {t("boDashboard:noViewsYet")}
                    </Typography>
                    <Stack divider={<Divider />}>
                      {data.productsHealth.zeroViews.slice(0, 5).map((p) => (
                        <ProductRow
                          key={p.productId}
                          product={p}
                          metricLabel={t("boDashboard:needsBetterTitlePhotos")}
                          metricValue={t("boDashboard:zeroViews")}
                        />
                      ))}
                    </Stack>
                  </Grid>
                )}
              </Grid>
            </SectionCard>
          )}
        </>
      ) : null}
    </Box>
  );
}
